import React, { useEffect, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { InkSection } from './shared';

/**
 * Content → Design → Reader questions ("tinycms UX optimization review v12",
 * app/TinyCMS App Prototype → Design): reader questions on the public site,
 * answered only from what the site has published (apiserver
 * atomizer/services/readerChat) — as a chat button on every page, an Ask page
 * (/ask, "Ask" at the end of the site header), or both.
 *
 * Comes with the Pro plan and needs Atomize; the server decides both on every
 * question, this screen only explains. Account admins switch it on and set it
 * up; any member can try it and see what readers asked. Changes apply to the
 * site within about a minute — no theme publish (unlike the design above it).
 *
 * Suggested questions are written here by the owner and never taken from what
 * readers ask, so nobody can put words on the site by asking.
 */

const READER_CHAT_FIELDS = `
  eligible ineligibleReason enabled mode onMiss contactPath suggestions canManage updatedAt
  insights { asked30d answered30d helpful30d notHelpful30d notCovered { question times } }
`;

const READER_CHAT = gql`
  query ReaderChat($accountId: ID!) {
    readerChat(accountId: $accountId) { ${READER_CHAT_FIELDS} }
  }
`;

// Drafts from the site's recent topics, for the owner to edit and save.
const READER_CHAT_SUGGESTION_DRAFTS = gql`
  query ReaderChatSuggestionDrafts($accountId: ID!) {
    readerChatSuggestionDrafts(accountId: $accountId)
  }
`;

const UPDATE_READER_CHAT = gql`
  mutation UpdateReaderChat($accountId: ID!, $input: ReaderChatInput!) {
    updateReaderChat(accountId: $accountId, input: $input) { ${READER_CHAT_FIELDS} }
  }
`;

const TRY_READER_CHAT = gql`
  mutation TryReaderChat($accountId: ID!, $question: String!) {
    tryReaderChat(accountId: $accountId, question: $question) {
      status
      answer
      message
      onMiss
      contactPath
      sources {
        n
        title
        path
        section
        date
      }
      related {
        n
        title
        path
        section
        date
      }
    }
  }
`;

interface Source {
  n: number;
  title: string;
  path: string;
  section: string | null;
  date: string | null;
}
interface Reply {
  status: string;
  answer: string | null;
  message: string | null;
  sources: Source[];
  related: Source[];
}
interface Settings {
  eligible: boolean;
  ineligibleReason: string | null;
  enabled: boolean;
  mode: 'chat' | 'page' | 'both';
  onMiss: 'contact' | 'say';
  contactPath: string | null;
  suggestions: string[];
  canManage: boolean;
  insights: {
    asked30d: number;
    answered30d: number;
    helpful30d: number;
    notHelpful30d: number;
    notCovered: Array<{ question: string; times: number }>;
  };
}

const MAX_SUGGESTIONS = 4;

const MODES = [
  ['chat', 'Chat button'],
  ['page', 'Ask page'],
  ['both', 'Both'],
] as const;

/** "Oct 18", as the site's chat shows a source's date. */
const shortDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
};

/** An answer's text with its [n] markers drawn as small numbered chips (text only, never HTML). */
function AnswerText({ text, count }: { text: string; count: number }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of Array.from(text.matchAll(/\[(\d{1,2})\]/g))) {
    const n = Number(m[1]);
    if (n < 1 || n > count) continue;
    if (m.index! > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    parts.push(
      <span key={key++} className="ink-rq-cite">
        {n}
      </span>
    );
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{parts}</>;
}

export const ReaderQuestions: React.FC<{ accountId: string; siteUrl: string | null }> = ({
  accountId,
  siteUrl,
}) => {
  const { data, loading, error } = useQuery<{ readerChat: Settings }>(READER_CHAT, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [update, { loading: saving }] = useMutation(UPDATE_READER_CHAT);
  const [tryIt, { loading: asking }] = useMutation<{ tryReaderChat: Reply }>(TRY_READER_CHAT);
  const [loadDrafts, { loading: drafting }] = useLazyQuery<{
    readerChatSuggestionDrafts: string[];
  }>(READER_CHAT_SUGGESTION_DRAFTS, { fetchPolicy: 'network-only' });
  // With nothing saved, readers see questions from the site's recent topics.
  const { data: autoData } = useQuery<{ readerChatSuggestionDrafts: string[] }>(
    READER_CHAT_SUGGESTION_DRAFTS,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  const autoSuggestions = autoData?.readerChatSuggestionDrafts || [];

  const rc = data?.readerChat;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [contactPath, setContactPath] = useState('');
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<{ q: string; reply: Reply } | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!rc) return;
    setSuggestions(rc.suggestions);
    setContactPath(rc.contactPath || '');
  }, [rc]);

  // Not a tinycms site, or the server predates reader chat: no section.
  if (error && !rc) return null;
  if (loading && !rc) return null;
  if (!rc) return null;

  const canEdit = rc.canManage;
  const save = async (input: Record<string, unknown>, ok?: string) => {
    setMessage(null);
    try {
      await update({
        variables: { accountId, input },
        // The settings have no id for Apollo to merge on: write them back by hand.
        update: (cache, { data: saved }) => {
          if (saved?.updateReaderChat) {
            cache.writeQuery({
              query: READER_CHAT,
              variables: { accountId },
              data: { readerChat: saved.updateReaderChat },
            });
          }
        },
      });
      if (ok) setMessage({ tone: 'ok', text: ok });
    } catch (e: any) {
      setMessage({
        tone: 'error',
        text: e?.graphQLErrors?.[0]?.message || 'Couldn’t save. Please try again.',
      });
    }
  };
  const ask = async (q: string) => {
    const text = q.trim();
    if (!text) return;
    setMessage(null);
    try {
      const res = await tryIt({ variables: { accountId, question: text } });
      if (res.data) setAsked({ q: text, reply: res.data.tryReaderChat });
    } catch (e: any) {
      setMessage({
        tone: 'error',
        text: e?.graphQLErrors?.[0]?.message || 'Couldn’t ask right now.',
      });
    }
  };

  const savedSuggestions = rc.suggestions.join('\n');
  const suggestionsDirty =
    suggestions
      .map((s) => s.trim())
      .filter(Boolean)
      .join('\n') !== savedSuggestions;
  const contactDirty = (contactPath.trim() || null) !== (rc.contactPath || null);
  const liveLink = (path: string) => (siteUrl ? `${siteUrl.replace(/\/+$/, '')}${path}` : path);
  const host = siteUrl ? siteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '') : 'your site';
  const modeHelp = {
    chat: 'A chat button in the corner of every page. Good for quick questions while reading.',
    page: `A full page at ${host}/ask, added to the end of your site menu as “Ask”. Answers read like a short article, with cards for the articles used.`,
    both: 'The Ask page in your menu, plus the chat button on every other page.',
  }[rc.mode];
  const onWhere = {
    chat: 'the chat button is',
    page: 'the Ask page is',
    both: 'the Ask page and chat button are',
  }[rc.mode];

  const desc = !rc.eligible
    ? rc.ineligibleReason === 'atomizer'
      ? 'Needs Prism AI, which reads your articles so the chat can answer from them.'
      : 'Comes with the Pro plan.'
    : rc.enabled
      ? `On. Readers see ${onWhere.replace(/ (is|are)$/, '')} within a minute.`
      : 'Off. Readers can’t ask questions on the site.';

  return (
    <InkSection
      id="reader-questions"
      title="Reader questions"
      description="Visitors ask a question, and it answers only from what you’ve published, with links to the articles it used. Offer it as a chat button on every page, as its own Ask page, or both."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0 }}>
        {rc.eligible || rc.enabled ? (
          <button
            type="button"
            className="ink-site-ask"
            disabled={!canEdit || saving}
            onClick={() => void save({ enabled: !rc.enabled })}
            aria-pressed={rc.enabled}
          >
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                Answer reader questions on the site
              </span>
              <span className="ink-site-row-desc">
                {desc}
                {!canEdit ? ' Only an account admin can change this.' : null}
              </span>
            </span>
            <span className="ink-switch" data-on={rc.enabled} />
          </button>
        ) : (
          // Not on Pro, or Prism AI is off: a row that explains, with the way there.
          <div className="ink-site-ask" style={{ cursor: 'default' }}>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                Answer reader questions on the site
              </span>
              <span className="ink-site-row-desc">
                {desc}{' '}
                {rc.ineligibleReason === 'atomizer' ? (
                  <Link to="/prism-ai/research">Turn on Prism AI</Link>
                ) : (
                  <Link to="/settings/billing">See plans</Link>
                )}
              </span>
            </span>
            <span className="ink-switch" data-on={false} aria-hidden="true" />
          </div>
        )}

        {rc.eligible ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Where readers ask</span>
              <span className="ink-seg" style={{ width: 'max-content' }}>
                {MODES.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-active={rc.mode === value}
                    disabled={!canEdit || saving}
                    onClick={() => rc.mode !== value && void save({ mode: value })}
                  >
                    {label}
                  </button>
                ))}
              </span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                {modeHelp}
              </span>
              {rc.enabled && siteUrl ? (
                <span
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    font: '500 13.5px var(--ink-font)',
                  }}
                >
                  {rc.mode !== 'page' ? (
                    <a href={liveLink('/')} target="_blank" rel="noopener noreferrer">
                      Preview the chat button ↗
                    </a>
                  ) : null}
                  {rc.mode !== 'chat' ? (
                    <a href={liveLink('/ask')} target="_blank" rel="noopener noreferrer">
                      Preview the Ask page ↗
                    </a>
                  ) : null}
                </span>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Suggested questions</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Shown before a visitor types, and after each reply as next questions (ones they
                haven’t asked yet). Up to {MAX_SUGGESTIONS}, written by you.
              </span>
              <div className="ink-site-card" style={{ marginTop: 4, padding: '6px 16px' }}>
                {suggestions.map((s, i) => (
                  <div key={i} className="ink-rq-suggestion">
                    <input
                      className="ink-site-input"
                      value={s}
                      maxLength={120}
                      disabled={!canEdit}
                      placeholder="How do you make your picks?"
                      onChange={(e) =>
                        setSuggestions(suggestions.map((x, j) => (j === i ? e.target.value : x)))
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        className="ink-site-link"
                        onClick={() => setSuggestions(suggestions.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ))}
                {!suggestions.length ? (
                  <div className="ink-site-row-desc" style={{ padding: '10px 0' }}>
                    {autoSuggestions.length ? (
                      <>
                        None saved, so readers see these, from your recent topics:{' '}
                        <strong>{autoSuggestions.join(' · ')}</strong>. Save your own to replace
                        them.
                      </>
                    ) : (
                      <span style={{ color: 'var(--ink-amber)' }}>
                        No suggested questions, and no recent topics to draw them from, so readers
                        get none.
                      </span>
                    )}
                    {canEdit ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="ink-site-link"
                          disabled={drafting}
                          onClick={() =>
                            void loadDrafts({ variables: { accountId } }).then(({ data: d }) => {
                              const drafts = d?.readerChatSuggestionDrafts || [];
                              if (drafts.length) {
                                setSuggestions(drafts);
                                setMessage({
                                  tone: 'ok',
                                  text: 'Edit them, then Save questions. Until you save, readers keep seeing the automatic ones.',
                                });
                              } else {
                                setMessage({
                                  tone: 'error',
                                  text: 'No recent topics to draw on. Add a few questions yourself.',
                                });
                              }
                            })
                          }
                        >
                          {drafting ? 'Loading…' : 'Edit these'}
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {canEdit ? (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  {suggestions.length < MAX_SUGGESTIONS ? (
                    <button
                      type="button"
                      className="ink-site-link"
                      onClick={() => setSuggestions([...suggestions, ''])}
                    >
                      Add a question
                    </button>
                  ) : null}
                  {suggestionsDirty ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={saving}
                      onClick={() =>
                        void save(
                          { suggestions: suggestions.map((s) => s.trim()).filter(Boolean) },
                          'Suggested questions saved.'
                        )
                      }
                    >
                      Save questions
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>
                When the site doesn’t cover a question
              </span>
              <span className="ink-seg" style={{ width: 'max-content' }}>
                {(
                  [
                    ['contact', 'Point to your contact page'],
                    ['say', 'Just say so'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-active={rc.onMiss === value}
                    disabled={!canEdit || saving}
                    onClick={() => rc.onMiss !== value && void save({ onMiss: value })}
                  >
                    {label}
                  </button>
                ))}
              </span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                {rc.onMiss === 'contact'
                  ? 'It says it can’t find the answer and offers an “Ask the team” button that opens your contact page.'
                  : 'It says it can’t find the answer and suggests other questions.'}
              </span>
              {rc.onMiss === 'contact' ? (
                <label className="ink-site-label" style={{ maxWidth: 360, marginTop: 6 }}>
                  Contact page address
                  <span style={{ display: 'flex', gap: 10 }}>
                    <input
                      className="ink-site-input"
                      value={contactPath}
                      placeholder="/contact"
                      maxLength={120}
                      disabled={!canEdit}
                      onChange={(e) => setContactPath(e.target.value)}
                    />
                    {canEdit && contactDirty ? (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        disabled={saving}
                        onClick={() =>
                          void save({ contactPath: contactPath.trim() }, 'Contact page saved.')
                        }
                      >
                        Save
                      </button>
                    ) : null}
                  </span>
                  <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                    A page on your site. Without one, the button isn’t shown.
                  </span>
                </label>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Try it</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Answers from your live site, the same way readers get them
                {rc.enabled ? '' : ', even while the button is off'}.
              </span>
              <form
                style={{ display: 'flex', gap: 10, maxWidth: 560 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void ask(question);
                }}
              >
                <input
                  className="ink-site-input"
                  value={question}
                  maxLength={300}
                  placeholder={rc.suggestions[0] || 'Ask a question a reader might ask'}
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <button type="submit" className="ink-btn" disabled={asking || !question.trim()}>
                  {asking ? 'Asking…' : 'Ask'}
                </button>
              </form>
              {asked ? (
                <div className="ink-site-card ink-rq-reply">
                  <div className="ink-rq-q">{asked.q}</div>
                  <div className="ink-rq-a">
                    {asked.reply.status === 'answered' && asked.reply.answer ? (
                      <AnswerText text={asked.reply.answer} count={asked.reply.sources.length} />
                    ) : (
                      asked.reply.message
                    )}
                  </div>
                  {(asked.reply.status === 'answered'
                    ? asked.reply.sources
                    : asked.reply.related
                  ).map((s) => (
                    <a
                      key={`${s.n}-${s.path}`}
                      className="ink-rq-source"
                      href={liveLink(s.path)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="ink-rq-cite">{s.n}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', font: '500 13.5px var(--ink-font)' }}>
                          {s.title}
                        </span>
                        <span className="ink-site-row-desc">
                          {[s.section, shortDate(s.date)].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>What readers asked</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Last 30 days: {rc.insights.asked30d.toLocaleString()} question
                {rc.insights.asked30d === 1 ? '' : 's'}, {rc.insights.answered30d.toLocaleString()}{' '}
                answered
                {rc.insights.helpful30d + rc.insights.notHelpful30d
                  ? ` (Ask page: ${rc.insights.helpful30d} found it helpful, ${rc.insights.notHelpful30d} didn’t)`
                  : ''}
                . Only you see these; nothing readers ask is shown on the site.
              </span>
              {rc.insights.notCovered.length ? (
                <div className="ink-site-card" style={{ marginTop: 4 }}>
                  {rc.insights.notCovered.map((m) => (
                    <div key={m.question} className="ink-site-row">
                      <div
                        className="ink-site-row-title"
                        style={{ font: '400 14px var(--ink-font)' }}
                      >
                        {m.question}
                      </div>
                      <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                        Not on the site · asked {m.times === 1 ? 'once' : `${m.times} times`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        {message ? (
          <div
            className="ink-site-row-desc"
            role="status"
            style={{
              color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)',
              marginTop: 0,
            }}
          >
            {message.text}
          </div>
        ) : null}
      </div>
    </InkSection>
  );
};
